/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import makeStyles from '@mui/styles/makeStyles';
import StyledProps from '../../../common/theme/AppThemeInterface';
import { InstallationType, TerminalDetails, DataPath, TerminalTypeLabel } from '../../../../shared/entities/terminal';
import TerminalTabPanel from './TerminalTabPanel';
import { DetailsPanelGroupCollapseState } from '../../../../store/ui/reducer';
import Config from '../../../../shared/entities/config';
import {
  getKuTerminalOnAircraft,
  getKaTerminalOnAircraft,
  getCWS1TerminalOnAircraft,
  getCWS2TerminalOnAircraft,
  getViTerminalOnAircraft,
  getRteTerminalOnAircraft,
} from '../../../../utils/TerminalUtilities';
import { isNil } from 'lodash';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { selectSelectedTerminalTabIndex } from '../../../../store';
import { setSelectedTerminalTabIndex } from '../../../../store/ui/actions';
import { isActive } from '../../../../utils/ConnectionStatusUtils';
import appTheme from '../../../common/theme/appTheme.json';
import { Authorities } from '../../../../shared/entities/user';
import { DetailsPanelEnum } from '../../../../shared/entities/detailsPanel';

export const PANEL_TAB_HEIGHT_PX = 42;
const TAB_INDEX_OVERVIEW = 0;
const TAB_INDEX_KU = 2;
const TAB_INDEX_CWS2 = 1;

interface TerminalTabContainerProps extends TerminalTabContainerStateProps, TerminalTabContainerDispatchProps {}

export interface TerminalTabContainerStateProps {
  detailsPanelVariant: DetailsPanelEnum;
  tailDetailsPanelTerminal?: TerminalDetails;
  detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState;
  isFullTailViewMode: boolean;
  isNextSatellitePanelEnabled: boolean;
  isSatellitesInFOVEnabled: boolean;
  appConfig: Config;
  history: any;
  rolesForCurrentUser: Authorities[] | undefined;
}

export interface TerminalTabContainerDispatchProps {
  setDetailsPanelGroupCollapseState: (detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState) => {};
}

const useStyles = makeStyles({
  root: {
    height: `${PANEL_TAB_HEIGHT_PX}px`,
    minHeight: `${PANEL_TAB_HEIGHT_PX}px`,
  },
});

export const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-top: -1px;
  background-color: #465967;
  position: relative;
`;

export const StyledAppBar = styled(AppBar)`
  flexgrow: 1;
  background-color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
`;

export const StyledTabs = styled(Tabs)`
  .MuiTabs-indicator {
    background-color: #61ded8;
    height: 3px;
    z-index: 1;
  }
`;

export const StyledTabLabel = styled.span`
  font-weight: 400;
  position: absolute;
  left: 20px;
  top: 9px;
`;

export default function TerminalTabContainer({
  detailsPanelVariant,
  tailDetailsPanelTerminal,
  isSatellitesInFOVEnabled,
  isNextSatellitePanelEnabled,
  isFullTailViewMode,
  appConfig,
  detailsPanelGroupCollapseState,
  history,
  rolesForCurrentUser,
  setDetailsPanelGroupCollapseState,
}: TerminalTabContainerProps) {
  const classes = useStyles();

  const [isKuPlusKa, setKuPlusKa] = useState(false);
  const [isDualTerminal, setDualTerminal] = useState(false);

  // Tab content and metadata
  const [tabIndex, setTabIndex] = useState(0);
  const [tabLabelArray, setTabLabelArray] = useState([] as string[]);
  const [tabContentArray, setTabContentArray] = useState([] as TerminalDetails[]);

  // Redux Actions
  const dispatch = useDispatch();
  const selectedTerminalTabIndex = useSelector(selectSelectedTerminalTabIndex, shallowEqual);

  const handleTabChange = (event: React.ChangeEvent, tabIndex: number) => {
    dispatch(setSelectedTerminalTabIndex(tabIndex));
  };

  const valueOrDefault = (value: any, defaultValue: any) => {
    if (!isNil(value)) {
      return value;
    } else {
      return defaultValue;
    }
  };

  const getSatelliteInFovOffset = () => {
    let offset = 0;
    if ((isKuPlusKa && tabIndex === TAB_INDEX_KU) || (isDualTerminal && tabIndex === TAB_INDEX_CWS2)) {
      let otherTerminal = tabContentArray[tabIndex - 1];
      if (!isNil(otherTerminal.beamDisplayName)) {
        offset++;
      }
      if (!isNil(otherTerminal.satellitesInFOV)) {
        offset += otherTerminal.satellitesInFOV.length;
      }
    }
    return offset;
  };

  const buildOverviewTerminalDetails = useCallback((terminal: TerminalDetails) => {
    let primaryTerminal: TerminalDetails;
    let secondaryTerminal: TerminalDetails;

    if (!isNil(terminal)) {
      // Determine Ku and Ka terminal details
      let kuTerminal = getKuTerminalOnAircraft(terminal);
      let kaTerminal = getKaTerminalOnAircraft(terminal);

      // Create primary terminal details
      if (terminal.primaryDataPath === DataPath.KA) {
        primaryTerminal = { ...kaTerminal };
        primaryTerminal.terminalsOnAircraft = [{ ...kuTerminal }];
        secondaryTerminal = { ...kuTerminal };
      } else {
        primaryTerminal = { ...kuTerminal };
        primaryTerminal.terminalsOnAircraft = [{ ...kaTerminal }];
        secondaryTerminal = { ...kaTerminal };
      }

      // Set datapathUuid to be that of the selected terminal rather than the primary data path terminal
      primaryTerminal.datapathUuid = valueOrDefault(terminal.datapathUuid, primaryTerminal.datapathUuid);

      // Antenna angle fields
      primaryTerminal.headingDeg = valueOrDefault(primaryTerminal.headingDeg, secondaryTerminal.headingDeg);
      primaryTerminal.rollAngleDeg = valueOrDefault(primaryTerminal.rollAngleDeg, secondaryTerminal.rollAngleDeg);
      primaryTerminal.pitchAngleDeg = valueOrDefault(primaryTerminal.pitchAngleDeg, secondaryTerminal.pitchAngleDeg);

      // Aircraft status fields
      primaryTerminal.flightPhase = valueOrDefault(primaryTerminal.flightPhase, secondaryTerminal.flightPhase);
      primaryTerminal.altitudeFeet = valueOrDefault(primaryTerminal.altitudeFeet, secondaryTerminal.altitudeFeet);
      primaryTerminal.velocityKnots = valueOrDefault(primaryTerminal.velocityKnots, secondaryTerminal.velocityKnots);
      primaryTerminal.latitudeDeg = valueOrDefault(primaryTerminal.latitudeDeg, secondaryTerminal.latitudeDeg);
      primaryTerminal.longitudeDeg = valueOrDefault(primaryTerminal.longitudeDeg, secondaryTerminal.longitudeDeg);
      primaryTerminal.visibilityBlanking = valueOrDefault(
        primaryTerminal.visibilityBlanking,
        secondaryTerminal.visibilityBlanking,
      );

      // Calculate combined activity summary fields if both terminals are active
      if (isActive(kuTerminal.connectivityStatus) && isActive(kaTerminal.connectivityStatus)) {
        primaryTerminal.flDataRateBps =
          valueOrDefault(kuTerminal.flDataRateBps, 0) + valueOrDefault(kaTerminal.flDataRateBps, 0);
        primaryTerminal.rlDataRateBps =
          valueOrDefault(kuTerminal.rlDataRateBps, 0) + valueOrDefault(kaTerminal.rlDataRateBps, 0);
      }

      primaryTerminal.flDataUseBytes =
        valueOrDefault(kuTerminal.flDataUseBytes, 0) + valueOrDefault(kaTerminal.flDataUseBytes, 0);
      primaryTerminal.rlDataUseBytes =
        valueOrDefault(kuTerminal.rlDataUseBytes, 0) + valueOrDefault(kaTerminal.rlDataUseBytes, 0);

      // Take most recent source time
      if (kuTerminal.sourceTime || kaTerminal.sourceTime) {
        primaryTerminal.sourceTime = Math.max(kuTerminal.sourceTime || 0, kaTerminal.sourceTime || 0);
      }

      return primaryTerminal;
    }

    return {} as TerminalDetails;
  }, []);

  // Build the tab content and metadata
  // Decide which tabs to create and populate with terminal data
  useEffect(() => {
    let tabLabelArray = [] as string[];
    let tabContentArray = [] as TerminalDetails[];
    if (tailDetailsPanelTerminal) {
      if (tailDetailsPanelTerminal.installationType === InstallationType.KU_PLUS_KA) {
        setKuPlusKa(true);
        let kaTerminal = getKaTerminalOnAircraft(tailDetailsPanelTerminal);
        let kuTerminal = getKuTerminalOnAircraft(tailDetailsPanelTerminal);
        tabLabelArray = ['Overview', 'Ka Ant.', 'Ku Ant.'];
        tabContentArray = [buildOverviewTerminalDetails(tailDetailsPanelTerminal), kaTerminal, kuTerminal];
      } else if (tailDetailsPanelTerminal.installationType === InstallationType.DUAL_TERMINAL) {
        setDualTerminal(true);
        let cws1Terminal = getCWS1TerminalOnAircraft(tailDetailsPanelTerminal);
        let cws2Terminal = getCWS2TerminalOnAircraft(tailDetailsPanelTerminal);
        tabLabelArray = ['CWS1', 'CWS2'];
        tabContentArray = [cws1Terminal, cws2Terminal];
      } else if (tailDetailsPanelTerminal.installationType === InstallationType.HALONET_TERMINAL) {
        setDualTerminal(true);
        const viTerminal = getViTerminalOnAircraft(tailDetailsPanelTerminal);
        const rteTerminal = getRteTerminalOnAircraft(tailDetailsPanelTerminal);
        tabLabelArray = ['VI', 'RTE'];
        // The RTE datapath reports no position, so its tab shows the vehicle record with the RTE status.
        tabContentArray = [
          viTerminal,
          {
            ...tailDetailsPanelTerminal,
            terminalTypeLabel: TerminalTypeLabel.RTE,
            connectivityStatus: rteTerminal.connectivityStatus,
            sourceTime: rteTerminal.sourceTime,
            positionSourceTime: tailDetailsPanelTerminal.positionSourceTime ?? tailDetailsPanelTerminal.sourceTime,
            terminalsOnAircraft: [viTerminal],
          },
        ];
      }
    }
    setTabIndex(Math.max(selectedTerminalTabIndex || 0, 0));
    setTabLabelArray(tabLabelArray);
    setTabContentArray(tabContentArray);
  }, [
    selectedTerminalTabIndex,
    tailDetailsPanelTerminal,
    buildOverviewTerminalDetails,
    setKuPlusKa,
    setDualTerminal,
    setTabIndex,
    setTabLabelArray,
    setTabContentArray,
  ]);

  let showDetailedConnectionStats = !!rolesForCurrentUser?.includes(Authorities.ROLE_DETAILED_CONNECTION_STATS);

  return (
    <React.Fragment>
      {tabLabelArray.length > 0 ? (
        <StyledAppBar data-test="terminal_tab_container" elevation={0} position="static">
          <StyledTabs value={tabIndex} classes={{ root: classes.root }} onChange={handleTabChange}>
            {tabLabelArray.map((tabLabel: string, index: number) => (
              <Tab
                data-test={`terminal_tab_container__tab_${index}`}
                key={tabLabel}
                sx={{
                  minWidth: '33.33%',
                  height: `${PANEL_TAB_HEIGHT_PX}px`,
                  minHeight: `${PANEL_TAB_HEIGHT_PX}px`,
                  color: appTheme.primaryButtonTextColor,
                  opacity: 0.7,
                  lineHeight: '25px',
                  '&.Mui-selected': {
                    lineHeight: '25px',
                    color: appTheme.primaryButtonTextColor,
                    opacity: 1.0,
                  },
                }}
                label={<StyledTabLabel>{tabLabel}</StyledTabLabel>}
              />
            ))}
          </StyledTabs>
        </StyledAppBar>
      ) : null}
      {(isKuPlusKa || isDualTerminal || !isFullTailViewMode) && <Line />}
      {tabContentArray.length > 0 ? (
        tabContentArray.map((tabContent: TerminalDetails, index: number) => (
          <TerminalTabPanel
            key={index}
            hidden={index !== tabIndex}
            satellitesInFOVIndexOffset={getSatelliteInFovOffset()}
            tailDetailsPanelTerminal={tabContent}
            isKuPlusKa={isKuPlusKa}
            isDualTerminal={isDualTerminal}
            isOverviewMode={isKuPlusKa && index === TAB_INDEX_OVERVIEW}
            isSatellitesInFOVEnabled={isSatellitesInFOVEnabled}
            isNextSatellitePanelEnabled={isNextSatellitePanelEnabled}
            isFullTailViewMode={isFullTailViewMode}
            appConfig={appConfig}
            detailsPanelGroupCollapseState={detailsPanelGroupCollapseState}
            setDetailsPanelGroupCollapseState={setDetailsPanelGroupCollapseState}
            history={history}
            showDetailedConnectionStats={showDetailedConnectionStats}
            detailsPanelVariant={detailsPanelVariant}
          />
        ))
      ) : (
        <TerminalTabPanel
          hidden={false}
          tailDetailsPanelTerminal={tailDetailsPanelTerminal}
          isKuPlusKa={isKuPlusKa}
          isDualTerminal={isDualTerminal}
          isOverviewMode={false}
          isSatellitesInFOVEnabled={isSatellitesInFOVEnabled}
          isNextSatellitePanelEnabled={isNextSatellitePanelEnabled}
          isFullTailViewMode={isFullTailViewMode}
          appConfig={appConfig}
          detailsPanelGroupCollapseState={detailsPanelGroupCollapseState}
          setDetailsPanelGroupCollapseState={setDetailsPanelGroupCollapseState}
          history={history}
          showDetailedConnectionStats={showDetailedConnectionStats}
          detailsPanelVariant={detailsPanelVariant}
        />
      )}
    </React.Fragment>
  );
}
